/**
 * Appearance: the one axis that is yours, and a statement of the two that are not.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT USED TO OFFER THREE CHOICES. IT NOW OFFERS ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This menu shipped with Mode, Palette and Accent as three lists of radio rows, on the reasonable
 * belief that all three were preferences. Two of them are not: the palette and the accent are the
 * ORGANISATION's (migration 0058), set by an ancestor on the organisation drawer's Appearance tab
 * and served already-resolved on `GET /me`.
 *
 * So those two lists are gone rather than disabled, and that is the same rule the rail follows: a
 * control that cannot act is hidden, not greyed. Leaving them here would have been worse than a
 * dead control — it would have been a SECOND AUTHORITY over one fact, and the local one always
 * wins by being nearer. A person picks `zinc`, `/me` answers `thunder` on the next focus refetch,
 * and the console flips back with nothing to say why.
 *
 * ── SO WHY IS IT STILL A MENU AND NOT A TOGGLE AGAIN ─────────────────────────
 * Because "why is my console this colour" now has an answer, and this is the only place a person
 * meets it. The two organisation-owned axes are NAMED here, read-only, with the organisation that
 * owns them — which turns an unexplained repaint (the day a platform operator brands a tenant)
 * into a fact the reader can check. A bare sun/moon button would repaint without explanation.
 *
 * The cost is honest: light-to-dark is two clicks rather than one. It was two clicks before this
 * change too, and the thing bought is now a sentence rather than a picker.
 *
 * ── THE TRIGGER STILL READS AS THE MODE ──────────────────────────────────────
 * Sun or moon, because that is the axis an operator changes for a reason (a daylight office, a
 * print) rather than a preference, and it is the only one they can change at all.
 *
 * ── NO COLOUR SWATCHES, DELIBERATELY ─────────────────────────────────────────
 * A swatch would have to hard-code a hex per palette here, and `tokens.css` is the single source
 * of truth for every colour in this product — a second copy is exactly the drift its own header
 * warns about, and it would be a copy no contrast test measures. Names, and the page itself is
 * the preview.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { ACCENTS, PALETTES, useTheme, type Theme } from '@/app/theme';
import { useMe } from '@/lib/api/me';

const MODES: ReadonlyArray<{ id: Theme; label: string; icon: 'ic-sun' | 'ic-moon' }> = [
  { id: 'light', label: 'Light', icon: 'ic-sun' },
  { id: 'dark', label: 'Dark', icon: 'ic-moon' },
];

export function ThemeMenu(): ReactNode {
  const { theme, palette, accent, set } = useTheme();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);

  /* Dismiss on outside click and on Escape — the same contract `AccountMenu` keeps, and for
     the same reason: a menu that only closes by re-clicking its own trigger traps people. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const row = (selected: boolean): string =>
    `lm-hov flex w-full items-center gap-[9px] px-[14px] py-[7px] text-left text-[12.5px]${
      selected ? ' font-semibold' : ''
    }`;

  /* The names as the pickers spell them, so the sentence here and the tab in Access read the
     same word for the same value. Falling back to the id covers the one frame before `/me`
     lands, where the store holds the stored hint and the label list is still the authority. */
  const paletteLabel = PALETTES.find((p) => p.id === palette)?.label ?? palette;
  const accentLabel = ACCENTS.find((a) => a.id === accent)?.label ?? accent;
  const orgName = me.data?.organisation.name;

  /*
   * ── "SET BY YOUR ORGANISATION" IS HALF AN ANSWER IF YOU ARE THE ORGANISATION ─
   *
   * The pickers came out of this menu when the two axes became the organisation's, and they stay
   * out: a second authority over one fact is worse than a dead control, and the local one always
   * wins by being nearer. But the rule underneath moved on 2026-09-18 — an organisation may now
   * brand ITSELF (`modules/organisations/service.ts`, `id === ctx.homeOrgId || isStrictAncestor(…)`)
   * — so for a reader holding `tenants.edit` this panel was stating a constraint that is not one.
   *
   * WORDS AND NOT A LINK, deliberately. There is no router (`app/App.tsx` holds `tab` in state),
   * so a real link means threading a callback from `App` through `Shell` into a menu that today
   * needs nothing from either. The sentence names the path instead, and it is three clicks the
   * reader can follow — which is what the rest of this menu already does for the two facts above
   * it. Add the link with the router, not before it.
   *
   * `tenants.edit` is the whole test, and it is the server's own: `patchOrganisation` requires it
   * and then asks whether the target is yours or beneath you. Somebody without it is told the
   * same true thing they were told before.
   */
  const mayBrand = me.data?.permissions.includes('tenants.edit') === true;

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="lm-hov flex items-center justify-center rounded-[var(--radius-sm)]"
        style={{
          width: 'var(--header-ctl-h)',
          height: 'var(--header-ctl-h)',
          color: 'var(--muted-fg)',
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Appearance"
      >
        <Icon name={theme === 'dark' ? 'ic-sun' : 'ic-moon'} size={16} />
      </button>

      {open && (
        <div
          role="menu"
          className="lm-card"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            minWidth: 214,
            zIndex: 50,
            padding: 0,
            /* Two modes and a three-line footer no longer threaten a short viewport the way
               fifteen radio rows did, but the cap costs nothing and stops a long organisation
               name from being the thing that runs the menu off the bottom of the screen. */
            maxHeight: 'min(70vh, 460px)',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Legend>Appearance</Legend>
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="menuitemradio"
              aria-checked={theme === m.id}
              onClick={() => {
                set(m.id);
                setOpen(false);
              }}
              className={row(theme === m.id)}
            >
              <Icon name={m.icon} size={13} />
              <span className="flex-1">{m.label}</span>
              {theme === m.id && <Icon name="ic-check" size={13} />}
            </button>
          ))}

          <div className="border-t" style={{ borderColor: 'var(--border)' }} />

          {/*
            * STATED, NOT OFFERED. Two facts and the reason they are facts — see the header.
            * `role="none"` because these are not menu items: a screen reader walking the menu
            * should meet two radios and then a description, not four things that look pressable.
            */}
          <div role="none" className="px-[14px] pb-[10px] pt-[9px]">
            <div
              className="pb-[5px] text-[12px] font-semibold"
              style={{ color: 'var(--muted-fg)' }}
            >
              Set by your organisation
            </div>
            <dl className="flex flex-col gap-[2px] text-[12px]">
              <div className="flex items-baseline justify-between gap-[10px]">
                <dt style={{ color: 'var(--muted-fg)' }}>Palette</dt>
                <dd className="font-semibold">{paletteLabel}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-[10px]">
                <dt style={{ color: 'var(--muted-fg)' }}>Accent</dt>
                <dd className="font-semibold">{accentLabel}</dd>
              </div>
            </dl>
            <p className="mt-[6px] text-[11px] leading-[1.45]" style={{ color: 'var(--muted-fg)' }}>
              {orgName === undefined
                ? 'The palette and the accent are the organisation’s. Light and dark are yours, and stay in this browser.'
                : `The palette and the accent are ${orgName}’s, so everyone in it sees the same console. Light and dark are yours, and stay in this browser.`}
            </p>
            {mayBrand && (
              <p
                className="mt-[5px] text-[11px] leading-[1.45]"
                style={{ color: 'var(--muted-fg)' }}
              >
                {/* ONE BOLD ANCHOR, NOT A BOLD PATH. "Access → your organisation → Appearance"
                    set solid ran to three lines in a 214px menu and the arrows broke across
                    them, which reads as a heading rather than as a hint. The rail entry is the
                    only part the reader has to FIND; the rest is what they do once there. */}
                Yours to change: open{' '}
                <span style={{ color: 'var(--fg)', fontWeight: 600 }}>Access</span>, pick{' '}
                {orgName ?? 'your organisation'}, then the Appearance tab.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** A section heading inside the menu. Micro caps, the same register as the rail's groups. */
function Legend({ children }: { children: ReactNode }): ReactNode {
  return (
    <div
      className="px-[14px] pb-[4px] pt-[9px] text-[12px] font-semibold"
      style={{ color: 'var(--muted-fg)' }}
    >
      {children}
    </div>
  );
}
